import { V2Card, V2CardHeader, V2Button, V2FormActions, Alert } from '@sakhtar/design-system/react';
import {
  MODULE_STEP_KEYS,
  PREVIOUS_DATA_STEP_KEY,
  SETUP_STATUS_LABEL,
  SETUP_STEP_LABEL,
  TEAM_STEP_KEY,
  baseOrganizationPayloadOf,
  setupStepView,
  type SetupStepKey,
  type SetupView,
} from '../../lib/types/setup.js';

/**
 * Phase C review screen (C5 «مرور تنظیمات») — read-only summary of every
 * step already answered, each with its own «ویرایش» jump-back
 * (docs/agents/handoffs/onboarding-design-review.md, Part 2). No internal
 * IDs/codes are shown, matching that screen's own note. Finishing here
 * calls the same real `POST .../setup/complete` the backend already
 * requires every step to be COMPLETED or SKIPPED for.
 *
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Onboarding Team and Review Desktop.html §C5 "مرور
 *   تنظیمات" — re-verified 2026-09-20 by rendering the file directly (see
 *   `TeamStep.tsx`'s own note on this file's near-duplicate overlap with
 *   `Sakhtar Onboarding Team Desktop.html`).
 * archetype: read-only settings manifest, one card per step, per-card
 *   "ویرایش" jump-back.
 * status: PARTIAL_REAL — one summary line per section from the real
 *   `SetupView` (fiscal year name + VAT for base-organization; step status
 *   for every module/team/previous-data card); the board's richer
 *   per-module answer summary (every individual chip answer, not just
 *   status) is not built — this reads status only, honest about what
 *   `SetupView` actually carries rather than re-deriving per-field summary
 *   text client-side. No internal IDs/codes are ever shown, matching the
 *   board's own explicit rule.
 * state reference: ready (all steps rendered from the already-loaded
 *   `SetupView`) · error (finish/complete failed)
 */
export function ReviewStep({
  view,
  onEditStep,
  onFinish,
  busy,
  error,
}: {
  view: SetupView;
  onEditStep: (key: SetupStepKey) => void;
  onFinish: () => Promise<void>;
  busy: boolean;
  error: string | null;
}) {
  const org = baseOrganizationPayloadOf(setupStepView(view, 'base-organization'));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="تنظیمات پایه سازمان"
          subtitle={statusText(setupStepView(view, 'base-organization').status)}
          actions={<EditButton onClick={() => onEditStep('base-organization')} />}
        />
        <div style={{ padding: '0 20px 20px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
          {org.fiscalYear
            ? `سال مالی: ${org.fiscalYear.name} · ارزش افزوده: ${vatLabel(org.vat)}`
            : '—'}
        </div>
      </V2Card>

      {MODULE_STEP_KEYS.map((key) => (
        <V2Card key={key}>
          <V2CardHeader
            title={SETUP_STEP_LABEL[key]}
            subtitle={statusText(setupStepView(view, key).status)}
            actions={<EditButton onClick={() => onEditStep(key)} />}
          />
        </V2Card>
      ))}

      <V2Card>
        <V2CardHeader
          title={SETUP_STEP_LABEL[TEAM_STEP_KEY]}
          subtitle={statusText(setupStepView(view, TEAM_STEP_KEY).status)}
          actions={<EditButton onClick={() => onEditStep(TEAM_STEP_KEY)} />}
        />
      </V2Card>

      <V2Card>
        <V2CardHeader
          title={SETUP_STEP_LABEL[PREVIOUS_DATA_STEP_KEY]}
          subtitle={statusText(setupStepView(view, PREVIOUS_DATA_STEP_KEY).status)}
          actions={<EditButton onClick={() => onEditStep(PREVIOUS_DATA_STEP_KEY)} />}
        />
      </V2Card>

      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      <V2FormActions>
        <span style={{ marginInlineEnd: 'auto' }} />
        <V2Button variant="primary" icon="check" disabled={busy} onClick={() => void onFinish()}>
          {busy ? 'در حال تکمیل…' : 'تکمیل راه‌اندازی و ورود به سامانه'}
        </V2Button>
      </V2FormActions>
    </div>
  );
}

function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <V2Button variant="ghost" size="sm" onClick={onClick}>
      ویرایش
    </V2Button>
  );
}

function statusText(status: SetupView['steps'][number]['status']): string {
  return SETUP_STATUS_LABEL[status];
}

function vatLabel(vat: string | null): string {
  if (vat === 'enabled') return 'مشمول هستیم';
  if (vat === 'disabled') return 'مشمول نیستیم';
  if (vat === 'skipped') return 'بعداً بررسی می‌شود';
  return '—';
}
